import * as React from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useGetApiVersionQuery } from '../../app/athenaApiGen';
import { isActionEnabled } from '../utilities';

// Frontend version is injected at build time (frontend-ci.yml derives it from
// git tags and passes it as a Docker build-arg -> Dockerfile.frontend ENV ->
// Vite import.meta.env). Local `vite dev` has no build args, so it falls back
// to an em dash (—) rather than a string that could be mistaken for a real version.
const FRONTEND_VERSION = import.meta.env.VITE_APP_VERSION || '—';

// Build-time fallbacks for values that also come from /api/version. Used when
// the request is in-flight or has failed.
const SERVICES_VERSION_FALLBACK = import.meta.env.VITE_SERVICES_VERSION || '—';
const MPXJ_VERSION_FALLBACK = import.meta.env.VITE_MPXJ_VERSION || '—';

const Footer = () => {
  // Use the RTK Query hook so the request shares the app's auth/cache layer.
  // The hook returns undefined data while loading; fall back to build-time values.
  const { data } = useGetApiVersionQuery();

  // Gate only the release-version text behind the platform-admin role.
  // `admin-sitewide-access` is the surviving platform-admin superuser action
  // (the `admin-platform-*` keys were dropped in the services-0.11.0 ACL revert)
  // — same gate used for the Admin menu/landing and /admin routes. The footer
  // bar and copyright still render for everyone; only non-admins don't see the
  // Frontend/Services/Data-Model/MPXJ version strings.
  const actions = useSelector((state) => state.user.actions);
  const isPlatformAdmin = !!isActionEnabled(actions, 'admin-sitewide-access');

  const servicesVersion = data?.servicesApiVersion ?? SERVICES_VERSION_FALLBACK;
  // dataModelVersion comes from the DB `Version` table already carrying its own
  // leading "v" (e.g. "v1.9.7.12"), so it is rendered as-is — no extra "v" prefix.
  const dataModelVersion = data?.dataModelVersion ?? '—';
  const mpxjVersion = data?.mpxjVersion ?? MPXJ_VERSION_FALLBACK;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        pl: 3,
        pr: 3,
        minHeight: 24,
        height: '100%',
        flex: '0 0 5%',
        justifyContent: 'center',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        backgroundColor: '#154F85',
      }}
    >
      <Typography sx={{ color: '#FFFFFF', textAlign: 'center', fontWeight: 'bold', fontSize: '0.70rem' }}>
        Copyright &copy; 2026 Tyto Athene. All rights reserved
      </Typography>
      {isPlatformAdmin && (
        <Typography
          sx={{
            position: 'absolute',
            right: 24,
            color: 'rgba(255, 255, 255, 0.55)',
            fontSize: '0.65rem',
          }}
        >
          Frontend v{FRONTEND_VERSION} &middot; Services v{servicesVersion} &middot; Data Model {dataModelVersion} &middot; MPXJ v{mpxjVersion}
        </Typography>
      )}
    </Box>
  );
};

export default Footer;
